import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  Field,
  Metric,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  Regulation,
  RegulationDiff,
  RegulationSection,
  RegulationSectionInput,
  RegulationSource,
  RegulationSourceKind,
  RegulationVersion,
} from "@odingard/sdk-client";
import { ApiError } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { regulatoryApi } from "../../lib/api";
import {
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  describeDenial,
  useGovernedMutation,
} from "../../governance";
import { ChangeKindBadge, VersionStatusBadge, sourceKindLabel } from "./regulatoryLabels";

const SOURCE_KINDS: RegulationSourceKind[] = [
  "customer_document",
  "public_reference",
  "odingard_summary",
];

// Regulation detail (APP-004 R3 P4): versions, the sections and provenance
// behind each one, and the deterministic comparison between two of them.
//
// The diff is not computed here. Two versions are sent to the backend and the
// change list comes back keyed on section content hashes, so the same pair
// always compares the same way and the screen cannot quietly disagree with the
// assessment built from it.
export function GovRegulationDetail() {
  const { regulationId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [selected, setSelected] = useState<string | null>(null);
  const [compareFrom, setCompareFrom] = useState("");
  const [diff, setDiff] = useState<RegulationDiff | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffBusy, setDiffBusy] = useState(false);

  const regulation = useAsync<Regulation>(
    async () => (await regulatoryApi.regulation(orgId, regulationId)).data.regulation,
    [orgId, regulationId],
  );
  const versions = useAsync<RegulationVersion[]>(
    async () => (await regulatoryApi.versions(orgId, regulationId)).data.versions,
    [orgId, regulationId],
  );

  const chosen =
    (versions.data ?? []).find((v) => v.id === selected) ??
    (versions.data ?? []).find((v) => v.status === "active") ??
    (versions.data ?? [])[0] ??
    null;
  const chosenId = chosen?.id ?? "";

  const sections = useAsync<RegulationSection[]>(
    async () => (chosenId ? (await regulatoryApi.sections(orgId, chosenId)).data.sections : []),
    [orgId, chosenId],
  );
  const sources = useAsync<RegulationSource[]>(
    async () => (chosenId ? (await regulatoryApi.sources(orgId, chosenId)).data.sources : []),
    [orgId, chosenId],
  );

  const compare = async () => {
    if (!compareFrom || !chosenId) return;
    setDiffBusy(true);
    setDiffError(null);
    try {
      setDiff((await regulatoryApi.diff(orgId, compareFrom, chosenId)).data.diff);
    } catch (err: unknown) {
      setDiff(null);
      setDiffError(err instanceof ApiError ? describeDenial(err) : "Unexpected error.");
    } finally {
      setDiffBusy(false);
    }
  };

  const reloadVersion = () => {
    versions.reload();
    sections.reload();
    sources.reload();
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{regulation.data?.name ?? "Regulation"}</h1>
          <p>
            {regulation.data ? (
              <>
                <code>{regulation.data.regulationKey}</code> ·{" "}
                {regulation.data.jurisdiction || "Jurisdiction not stated"} ·{" "}
                {regulation.data.authority || "Authority not stated"} ·{" "}
              </>
            ) : null}
            <Link to="/studio/governance/regulations">Back to regulations</Link>
          </p>
          {regulation.data?.description ? (
            <p className="muted-note">{regulation.data.description}</p>
          ) : null}
        </div>
        <div className="badge-row">
          <CreateVersion orgId={orgId} regulationId={regulationId} onDone={reloadVersion} />
        </div>
      </div>

      <GovPanel
        title="Versions"
        state={versions}
        loadingLabel="Loading versions…"
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="No versions yet"
        emptyDescription="Create a version, import its sections and activate it once the text is complete."
      >
        {(rows) => (
          <DataTable
            caption="Regulation versions"
            onRowClick={(row) => setSelected(row.id)}
            columns={[
              {
                key: "label",
                header: "Version",
                render: (row) => (
                  <>
                    <strong>{row.versionLabel}</strong>
                    <div className="badge-row">
                      <VersionStatusBadge status={row.status} />
                      {row.id === chosenId ? <Badge tone="info">Shown below</Badge> : null}
                    </div>
                  </>
                ),
              },
              { key: "effective", header: "In force from", render: (row) => formatDate(row.effectiveDate) },
              { key: "published", header: "Published", render: (row) => formatDate(row.publishedDate) },
              { key: "sections", header: "Sections", render: (row) => row.sectionCount },
              {
                key: "hash",
                header: "Content hash",
                render: (row) => <code>{row.contentHash.slice(0, 16)}…</code>,
              },
              {
                key: "actions",
                header: "",
                render: (row) =>
                  row.status === "draft" ? (
                    <GovernedAction
                      permission={GOVERNANCE_PERMISSIONS.regulationsManage}
                      label="Activate"
                      title="Activate this version"
                      description="Makes this the version the organization is measured against."
                      consequence="The version currently in force is superseded, and sections can no longer be imported into this one."
                      confirmLabel="Activate"
                      busyLabel="Activating…"
                      onConfirm={() => regulatoryApi.activateVersion(orgId, row.id)}
                      onDone={reloadVersion}
                    />
                  ) : null,
              },
            ]}
            rows={rows}
          />
        )}
      </GovPanel>

      {chosen ? (
        <>
          <Card>
            <CardHeader
              title={`Sections of ${chosen.versionLabel}`}
              action={
                chosen.status === "draft" ? (
                  <ImportSections orgId={orgId} versionId={chosen.id} onDone={reloadVersion} />
                ) : null
              }
            />
            <CardBody>
              <p className="muted-note">
                Each section is hashed over its key, title and body, and the version hash covers
                every section hash. Identical text imported twice produces the same hash, which is
                what makes a comparison reproducible.
              </p>
              <GovPanel
                bare
                state={sections}
                loadingLabel="Loading sections…"
                isEmpty={(rows) => rows.length === 0}
                emptyTitle="No sections imported"
                emptyDescription="Import the sections of the version you hold. Only a draft accepts them."
              >
                {(rows) => (
                  <DataTable
                    caption="Sections"
                    columns={[
                      { key: "key", header: "Section", render: (row) => <code>{row.sectionKey}</code> },
                      { key: "title", header: "Title", render: (row) => row.title },
                      {
                        key: "body",
                        header: "Text",
                        render: (row) =>
                          row.body ? (
                            <span className="muted-note">{row.body}</span>
                          ) : (
                            <span className="muted-note">No text held</span>
                          ),
                      },
                      {
                        key: "hash",
                        header: "Hash",
                        render: (row) => <code>{row.contentHash.slice(0, 12)}…</code>,
                      },
                    ]}
                    rows={rows}
                  />
                )}
              </GovPanel>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Source lineage"
              action={<AddSource orgId={orgId} versionId={chosen.id} onDone={sources.reload} />}
            />
            <CardBody>
              <p className="muted-note">
                Where this version came from. A document source names a record in Documents; no
                file bytes ever reach Policy AI.
              </p>
              <GovPanel
                bare
                state={sources}
                loadingLabel="Loading sources…"
                isEmpty={(rows) => rows.length === 0}
                emptyTitle="No sources recorded"
                emptyDescription="Record where the text came from so an auditor can trace it."
              >
                {(rows) => (
                  <DataTable
                    caption="Sources"
                    columns={[
                      { key: "kind", header: "Kind", render: (row) => sourceKindLabel(row.sourceKind) },
                      {
                        key: "ref",
                        header: "Reference",
                        render: (row) =>
                          row.documentId ? (
                            <code>{row.documentId}</code>
                          ) : row.referenceUrl ? (
                            <span className="muted-note">{row.referenceUrl}</span>
                          ) : (
                            <span className="muted-note">Not recorded</span>
                          ),
                      },
                      { key: "citation", header: "Citation", render: (row) => row.citation || "Not stated" },
                      { key: "added", header: "Recorded", render: (row) => formatDate(row.createdAt) },
                    ]}
                    rows={rows}
                  />
                )}
              </GovPanel>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Compare versions" />
            <CardBody>
              <div className="filter-row">
                <Field
                  label="Compare with"
                  help={`Compared against ${chosen.versionLabel}, the version shown above.`}
                >
                  {(id) => (
                    <Select id={id} value={compareFrom} onChange={(e) => setCompareFrom(e.target.value)}>
                      <option value="">Choose an earlier version</option>
                      {(versions.data ?? [])
                        .filter((v) => v.id !== chosen.id)
                        .map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.versionLabel}
                          </option>
                        ))}
                    </Select>
                  )}
                </Field>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!compareFrom || diffBusy}
                    onClick={() => void compare()}
                  >
                    {diffBusy ? "Comparing…" : "Compare"}
                  </Button>
                </div>
              </div>
              {diffError ? (
                <p className="ods-denial" role="alert">
                  {diffError}
                </p>
              ) : null}
              {diff ? <DiffView diff={diff} /> : null}
            </CardBody>
          </Card>
        </>
      ) : null}
    </>
  );
}

function DiffView({ diff }: { diff: RegulationDiff }) {
  return (
    <div data-testid="regulation-diff">
      <div className="metric-grid">
        <div className="metric-card">
          <Metric value={diff.addedCount} label="Sections added" />
        </div>
        <div className="metric-card">
          <Metric value={diff.modifiedCount} label="Sections modified" />
        </div>
        <div className="metric-card">
          <Metric value={diff.removedCount} label="Sections removed" />
        </div>
      </div>
      <p className="muted-note">
        {diff.summary} Computed by <code>{diff.model}</code> {diff.analyzerVersion} over the two
        content hashes, so the same pair always compares the same way. Inputs hash{" "}
        <code>{diff.inputsHash.slice(0, 16)}…</code>.
      </p>
      {diff.changes.length === 0 ? (
        <p className="muted-note">Nothing changed between these versions.</p>
      ) : (
        <DataTable
          caption="Section changes"
          columns={[
            {
              key: "section",
              header: "Section",
              render: (row) => (
                <>
                  <code>{row.sectionKey}</code>
                  <div className="badge-row">
                    <ChangeKindBadge kind={row.kind} />
                  </div>
                </>
              ),
            },
            { key: "title", header: "Title", render: (row) => row.title },
            {
              key: "hashes",
              header: "Hashes",
              render: (row) => (
                <div className="muted-note">
                  {row.fromHash ? <code>{row.fromHash.slice(0, 10)}…</code> : "absent"}
                  {" → "}
                  {row.toHash ? <code>{row.toHash.slice(0, 10)}…</code> : "absent"}
                </div>
              ),
            },
            {
              key: "terms",
              header: "Terms",
              render: (row) => (
                <>
                  {row.addedTerms.length > 0 ? (
                    <div className="muted-note">Added: {row.addedTerms.slice(0, 8).join(", ")}</div>
                  ) : null}
                  {row.removedTerms.length > 0 ? (
                    <div className="muted-note">
                      Removed: {row.removedTerms.slice(0, 8).join(", ")}
                    </div>
                  ) : null}
                </>
              ),
            },
          ]}
          rows={diff.changes.map((change) => ({ ...change, id: change.sectionKey }))}
        />
      )}
    </div>
  );
}

/** Parses the textarea import format: one `key | title | body` line per section. */
export function parseSections(value: string): RegulationSectionInput[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [key = "", title = "", ...rest] = line.split("|").map((part) => part.trim());
      return { sectionKey: key, title, body: rest.join(" | ") };
    });
}

function CreateVersion({
  orgId,
  regulationId,
  onDone,
}: {
  orgId: string;
  regulationId: string;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [versionLabel, setLabel] = useState("");
  const [effectiveDate, setEffective] = useState("");
  const [publishedDate, setPublished] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.regulationsManage);

  const submit = async () => {
    const ok = await mutation.run(() =>
      regulatoryApi.createVersion(orgId, regulationId, {
        versionLabel: versionLabel.trim(),
        effectiveDate: effectiveDate || null,
        publishedDate: publishedDate || null,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setLabel("");
    setEffective("");
    setPublished("");
    onDone();
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        New version
      </Button>
      <Dialog
        open={open}
        title="New version"
        description="Creates a draft. Import its sections, then activate it when the text is complete."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={versionLabel.trim().length === 0 || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Creating…" : "Create version"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Label" help="How your team refers to this edition.">
          {(id) => <TextInput id={id} value={versionLabel} onChange={(e) => setLabel(e.target.value)} />}
        </Field>
        <Field label="In force from">
          {(id) => (
            <TextInput
              id={id}
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffective(e.target.value)}
            />
          )}
        </Field>
        <Field label="Published">
          {(id) => (
            <TextInput
              id={id}
              type="date"
              value={publishedDate}
              onChange={(e) => setPublished(e.target.value)}
            />
          )}
        </Field>
      </Dialog>
    </>
  );
}

function ImportSections({
  orgId,
  versionId,
  onDone,
}: {
  orgId: string;
  versionId: string;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.regulationsManage);
  const parsed = parseSections(value);

  const submit = async () => {
    const ok = await mutation.run(() => regulatoryApi.importSections(orgId, versionId, parsed));
    if (!ok) return;
    setOpen(false);
    setValue("");
    onDone();
  };

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Import sections
      </Button>
      <Dialog
        open={open}
        title="Import sections"
        description="Replaces this draft's sections and recomputes the version hash."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={parsed.length === 0 || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Importing…" : `Import ${parsed.length} sections`}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <p className="ods-denial" style={{ marginTop: 0 }}>
          This text belongs to your organization and stays inside this tenant. Only paste text you
          are licensed to hold.
        </p>
        <Field
          label="Sections"
          help="One per line, as key, title and text separated by a vertical bar."
        >
          {(id) => (
            <TextArea id={id} rows={8} value={value} onChange={(e) => setValue(e.target.value)} />
          )}
        </Field>
      </Dialog>
    </>
  );
}

function AddSource({
  orgId,
  versionId,
  onDone,
}: {
  orgId: string;
  versionId: string;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [sourceKind, setKind] = useState<RegulationSourceKind>("public_reference");
  const [documentId, setDocumentId] = useState("");
  const [referenceUrl, setUrl] = useState("");
  const [citation, setCitation] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.regulationsManage);

  const submit = async () => {
    const ok = await mutation.run(() =>
      regulatoryApi.addSource(orgId, versionId, {
        sourceKind,
        documentId: documentId.trim() || null,
        referenceUrl: referenceUrl.trim() || null,
        citation: citation.trim(),
      }),
    );
    if (!ok) return;
    setOpen(false);
    setDocumentId("");
    setUrl("");
    setCitation("");
    onDone();
  };

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Add source
      </Button>
      <Dialog
        open={open}
        title="Add a source"
        description="Records where this version of the text came from."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={mutation.busy} onClick={() => void submit()}>
              {mutation.busy ? "Saving…" : "Add source"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Kind">
          {(id) => (
            <Select
              id={id}
              value={sourceKind}
              onChange={(e) => setKind(e.target.value as RegulationSourceKind)}
            >
              {SOURCE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {sourceKindLabel(kind)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {sourceKind === "customer_document" ? (
          <Field label="Document" help="The identifier of a record already held in Documents.">
            {(id) => (
              <TextInput id={id} value={documentId} onChange={(e) => setDocumentId(e.target.value)} />
            )}
          </Field>
        ) : null}
        {sourceKind !== "customer_document" ? (
          <Field label="Reference">
            {(id) => <TextInput id={id} value={referenceUrl} onChange={(e) => setUrl(e.target.value)} />}
          </Field>
        ) : null}
        <Field label="Citation">
          {(id) => <TextInput id={id} value={citation} onChange={(e) => setCitation(e.target.value)} />}
        </Field>
      </Dialog>
    </>
  );
}
